import React, { useState, useEffect, useRef } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './ScaffoldEditor.css';

/**
 * ScaffoldEditor - React implementation of scaffold creation/editing
 * Matches Angular scaffoldEditor directive from video-vetting.js
 */
const ScaffoldEditor = ({
  show,
  onHide,
  scaffold = null, // Existing scaffold for editing, null for new scaffold
  editQuestionID = null, // If adding scaffold from question editor
  onSave, // Callback when scaffold is saved (standalone scaffold list)
  onSaveToQuestion, // When editQuestionID set: add scaffold to question and save question (no AddScaffold API)
  onLink, // Callback when existing scaffold is linked
  mode = 'create' // 'create' or 'link'
}) => {
  const [description, setDescription] = useState('');
  // On question page (Add Scaffold): default to Link; otherwise use mode prop
  const [scaffoldMode, setScaffoldMode] = useState(() =>
    (editQuestionID && !scaffold) ? 'link' : (mode === 'link' ? 'link' : 'create')
  );
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topics, setTopics] = useState([]);
  const [fileType, setFileType] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedScreenshot, setUploadedScreenshot] = useState(null);
  const [videoType, setVideoType] = useState('Q'); // 'Q' for Question, 'T' for Topic
  const [linkedScaffold, setLinkedScaffold] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [allScaffolds, setAllScaffolds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadInfo, setUploadInfo] = useState('');
  const [error, setError] = useState(null);
  const [camtasiaData, setCamtasiaData] = useState(null); // For Camtasia files

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const screenshotInputRef = useRef(null);
  const uploadAbortRef = useRef(null);

  /**
   * Initialize TinyMCE editor
   */
  useEffect(() => {
    if (show && window.tinymce) {
      const initEditor = async () => {
        if (window.tinymce.get('scaffoldArea')) {
          window.tinymce.get('scaffoldArea').remove();
        }

        window.tinymce.init({
          selector: '#scaffoldArea',
          license_key: 'gpl',
          branding: false,
          promotion: false,
          extended_valid_elements: 'span[class|data-alt|data-tex|contenteditable|style|id]',
          external_plugins: {
            'example': '../editor/plugins/example/plugin.min.js',
            'getfrommobile': '../editor/plugins/getfrommobile/plugin.min.js'
          },
          toolbar: "undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | example_key | getfrommobile_key",
          plugins: 'example getfrommobile',
          setup: (ed) => {
            editorRef.current = ed;

            // Re-wrap LaTeX \( ... \) patterns from Word paste into .mathformula spans
            // Phase 1: DOM text-node walk (multiple formulas in same text node)
            // Phase 2: innerHTML fallback (Word splits \( and \) across different elements)
            const rewrapLatexInNode = (node) => {
              if (!node || (node.querySelector && node.querySelector('.mathformula'))) return false;
              const latexPattern = /\\\(((?:(?!\\\()[\s\S])+?)\\\)/g;
              let changed = false;

              // Phase 1: DOM text-node walk
              const textNodes = [];
              const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null, false);
              while (walker.nextNode()) textNodes.push(walker.currentNode);
              for (let i = 0; i < textNodes.length; i++) {
                const tNode = textNodes[i];
                const text = tNode.textContent;
                if (text.indexOf('\\(') === -1) continue;
                latexPattern.lastIndex = 0;
                const parts = [];
                let lastIdx = 0, m;
                while ((m = latexPattern.exec(text)) !== null) {
                  if (m.index > lastIdx) parts.push({ type: 'text', val: text.substring(lastIdx, m.index) });
                  parts.push({ type: 'formula', val: m[0] });
                  lastIdx = latexPattern.lastIndex;
                }
                if (parts.length === 0) continue;
                if (lastIdx < text.length) parts.push({ type: 'text', val: text.substring(lastIdx) });
                const frag = document.createDocumentFragment();
                for (let j = 0; j < parts.length; j++) {
                  if (parts[j].type === 'text') {
                    frag.appendChild(document.createTextNode(parts[j].val));
                  } else {
                    const span = document.createElement('span');
                    span.className = 'mathformula';
                    span.setAttribute('contenteditable', 'false');
                    span.setAttribute('data-tex', parts[j].val);
                    span.textContent = parts[j].val;
                    frag.appendChild(span);
                  }
                }
                tNode.parentNode.replaceChild(frag, tNode);
                changed = true;
              }
              if (changed) return true;

              // Phase 2: innerHTML fallback (Word splits LaTeX across elements)
              const fullText = node.textContent || '';
              if (fullText.indexOf('\\(') === -1) return false;
              const html = node.innerHTML;
              latexPattern.lastIndex = 0;
              const newHtml = html.replace(latexPattern, (match) => {
                const tex = match.replace(/<[^>]+>/g, '');
                return '<span class="mathformula" contenteditable="false" data-tex="' +
                  tex.replace(/"/g, '&quot;') + '">' + tex + '</span>';
              });
              if (newHtml !== html) {
                node.innerHTML = newHtml;
                return true;
              }
              return false;
            };
            let pasteHandled = false;
            ed.on('PastePostProcess', (e) => {
              if (rewrapLatexInNode(e.node)) pasteHandled = true;
            });
            ed.on('paste', () => {
              pasteHandled = false;
              setTimeout(() => {
                if (pasteHandled) return;
                const body = ed.getBody();
                if (!body || body.querySelector('.mathformula')) return;
                if (body.textContent.indexOf('\\(') === -1) return;
                if (rewrapLatexInNode(body)) ed.undoManager.add();
              }, 200);
            });
            
            // Use Description or description (API casing may vary)
            const initialContent = (scaffold && (scaffold.Description ?? scaffold.description ?? '')) || '';
            // Set when editor is ready (init runs after body exists)
            ed.on('init', () => {
              if (initialContent) {
                ed.setContent(initialContent, { format: 'raw' });
                setDescription(initialContent);
              }
            });
            // Also set in setup in case init already ran (e.g. re-init)
            if (initialContent) {
              ed.setContent(initialContent, { format: 'raw' });
            }

            // Update state on content change
            ed.on('keydown', () => {
              setDescription(ed.getContent({ format: 'raw' }));
            });

            ed.on('change', () => {
              setDescription(ed.getContent({ format: 'raw' }));
            });

            // Close dialog on ESC
            ed.on('keydown', (e) => {
              if (e.key === 'Escape') {
                onHide();
              }
            });
          }
        });
      };

      initEditor();
    }

    return () => {
      if (window.tinymce && window.tinymce.get('scaffoldArea')) {
        window.tinymce.get('scaffoldArea').remove();
      }
    };
  }, [show, scaffold]);

  /**
   * Load topics for scaffold selection
   */
  useEffect(() => {
    if (show && scaffoldMode === 'create' && !editQuestionID) {
      loadTopics();
    }
  }, [show, scaffoldMode]);

  /**
   * Load all scaffolds for linking
   */
  useEffect(() => {
    if (show && scaffoldMode === 'link') {
      loadAllScaffolds();
    }
  }, [show, scaffoldMode]);

  /**
   * Initialize component data when scaffold is provided for editing
   */
  useEffect(() => {
    if (scaffold) {
      setScaffoldMode('create'); // Edit mode: show Submit/Draft buttons, not Link
      const desc = scaffold.Description ?? scaffold.description ?? '';
      setDescription(desc);
      setFileType(scaffold.TypeDesc ?? scaffold.type ?? null);
      setUploadedFile(scaffold.FileID ?? scaffold.filePath ?? null);
      setUploadedScreenshot(scaffold.screenshot ?? scaffold.Screenshot ?? null);
      setVideoType(scaffold.videoType ?? scaffold.VideoType ?? 'Q');
      
      if (scaffold.TopicID != null || scaffold.topicID != null) {
        setSelectedTopic(scaffold.TopicID ?? scaffold.topicID);
      }
    } else {
      // Reset for new scaffold
      setDescription('');
      setFileType(null);
      setUploadedFile(null);
      setUploadedScreenshot(null);
      setVideoType('Q');
      setSelectedTopic(null);
      setLinkedScaffold(null);
      setCamtasiaData(null);
    }
  }, [scaffold]);

  /**
   * When a scaffold is selected in link mode, show its description in TinyMCE (sync in case editor wasn't ready on click)
   */
  useEffect(() => {
    if (!linkedScaffold || !show) return;
    const desc = linkedScaffold.Description ?? linkedScaffold.description ?? '';
    const ed = editorRef.current || (window.tinymce && window.tinymce.get('scaffoldArea'));
    if (ed && !ed.removed) {
      ed.setContent(desc, { format: 'raw' });
    }
  }, [linkedScaffold, show]);

  /**
   * Load network topics
   */
  const loadTopics = async () => {
    try {
      const token = getTokenForRequest();
      const url = token
        ? `./webservices/Network.svc/GetNetwork?token=${window.encodeToken(token)}`
        : './webservices/Network.svc/GetNetwork';

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load topics');
      }

      const result = await response.json();
      setTopics(result.nodes || []);
    } catch (error) {
      console.error('Error loading topics:', error);
      setError('Failed to load topics');
    }
  };

  /**
   * Load all scaffolds for linking
   */
  const loadAllScaffolds = async () => {
    try {
      const token = getTokenForRequest();
      
      const response = await fetch(`./webservices/Scaffold.svc/getScaffolds?token=${window.encodeToken(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load scaffolds');
      }

      const result = await response.json();
      setAllScaffolds(result || []);
    } catch (error) {
      console.error('Error loading scaffolds:', error);
      setError('Failed to load scaffolds');
    }
  };

  /**
   * Get S3 signature for file upload
   */
  const getS3Signature = async (fileName) => {
    try {
      const response = await fetch('./webservices/Authentication.svc/GetSignature', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get S3 signature');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting S3 signature:', error);
      throw error;
    }
  };

  /**
   * Upload file to S3
   */
  const uploadFileToS3 = async (file, fileType = 'main') => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      // Get S3 signature
      const signature = await getS3Signature(file.name);
      
      // Prepare form data for S3 upload
      const formData = new FormData();
      formData.append('key', signature.key);
      formData.append('AWSAccessKeyId', signature.AWSAccessKeyId || '');
      formData.append('acl', 'private');
      formData.append('success_action_status', '200');
      formData.append('policy', signature.policy);
      formData.append('signature', signature.signature);
      formData.append('file', file);

      // Determine S3 URL (should be configured globally in the host page)
      const s3Url = window.fileUploadUrl;
      if (!s3Url) {
        throw new Error('S3 upload URL not configured. Please set window.fileUploadUrl');
      }

      // Upload to S3 using fetch with progress tracking
      const xhr = new XMLHttpRequest();
      
      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200 || xhr.status === 204) {
            setUploading(false);
            setUploadProgress(0);
            setUploadInfo('');
            
            if (fileType === 'screenshot') {
              setUploadedScreenshot(signature.key);
            } else {
              setUploadedFile(signature.key);
              
              // Determine file type from extension
              const ext = file.name.split('.').pop().toLowerCase();
              if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(ext)) {
                setFileType('Video');
              } else if (['pdf'].includes(ext)) {
                setFileType('PDF');
              } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
                setFileType('Image');
              } else {
                setFileType('Document');
              }
            }
            
            resolve(signature.key);
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => {
          setUploading(false);
          setUploadProgress(0);
          reject(new Error('Upload error'));
        });

        xhr.addEventListener('abort', () => {
          setUploading(false);
          setUploadProgress(0);
          reject(new Error('Upload cancelled'));
        });

        uploadAbortRef.current = xhr;
        
        xhr.open('POST', s3Url);
        xhr.send(formData);
        
        setUploadInfo(`Uploading ${fileType === 'screenshot' ? 'screenshot' : 'file'}...`);
      });
    } catch (error) {
      setUploading(false);
      setUploadProgress(0);
      setError(`Failed to upload ${fileType === 'screenshot' ? 'screenshot' : 'file'}`);
      throw error;
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExt = file.name.split('.').pop().toLowerCase();

    // Check if it's a Camtasia zip file
    if (fileExt === 'zip') {
      // Handle Camtasia file (complex - needs zip parsing)
      setError('Camtasia file support coming soon');
      return;
    }

    try {
      await uploadFileToS3(file, 'main');
    } catch (error) {
      setError('Failed to upload file');
    }
  };

  /**
   * Handle screenshot selection
   */
  const handleScreenshotSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await uploadFileToS3(file, 'screenshot');
    } catch (error) {
      setError('Failed to upload screenshot');
    }
  };

  /**
   * Cancel upload
   */
  const cancelUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadInfo('');
  };

  /**
   * Handle scaffold search for linking
   */
  const handleScaffoldSelect = (selectedScaffold) => {
    setLinkedScaffold(selectedScaffold);
    const desc = selectedScaffold.Description ?? selectedScaffold.description ?? '';
    setDescription(desc);
    const ed = editorRef.current || (window.tinymce && window.tinymce.get('scaffoldArea'));
    if (ed && !ed.removed) {
      ed.setContent(desc, { format: 'raw' });
    }
  };

  /**
   * Strip HTML tags to plain text for display in lists
   */
  const htmlToPlainText = (html) => {
    if (html == null || typeof html !== 'string') return '';
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  /**
   * Filter scaffolds for search
   */
  const filteredScaffolds = allScaffolds.filter(s => 
    s.Status === 'A' && 
    (s.Description?.toLowerCase().includes(searchText.toLowerCase()) ||
     s.TypeDesc?.toLowerCase().includes(searchText.toLowerCase()))
  );

  /**
   * Returns true if HTML has no meaningful text (empty, whitespace-only, or only empty tags like <p></p> / <p><br></p>).
   */
  const isDescriptionEffectivelyEmpty = (html) => {
    if (html == null || typeof html !== 'string') return true;
    const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length === 0;
  };

  /**
   * Get description for save - same as old saveScaffolding: getContent() with no options.
   * Prefer editorRef (set in setup), then tinymce.get('scaffoldArea'), then read from body, then React state.
   */
  const getDescriptionForSave = () => {
    try {
      const ed = editorRef.current || (window.tinymce && window.tinymce.get('scaffoldArea'));
      if (ed && !ed.removed) {
        let content = ed.getContent();
        if ((content == null || content.length === 0) && ed.getBody()) {
          content = ed.getBody().innerHTML || '';
        }
        if (content != null && content.length > 0) return content;
      }
    } catch (_) {}
    return description || '';
  };

  /**
   * Handle save scaffold
   */
  const handleSave = async (saveMode) => {
    try {
      setError(null);
      const desc = getDescriptionForSave();

      if (desc == null || desc.length === 0 || isDescriptionEffectivelyEmpty(desc)) {
        setError('Description is mandatory. Please enter a description.');
        return;
      }

      if (scaffoldMode === 'link') {
        if (!linkedScaffold) {
          setError('Please select an existing scaffold');
          return;
        }

        if (onLink) {
          onLink(linkedScaffold, desc);
        }
        onHide();
        return;
      }

      if (!uploadedFile) {
        setError('Please upload a file');
        return;
      }

      if (!selectedTopic && !editQuestionID) {
        setError('Please select a topic');
        return;
      }

      if (editQuestionID && onSaveToQuestion) {
        onSaveToQuestion(editQuestionID, {
          desc,
          filePath: uploadedFile,
          fileType,
          screenshotPath: uploadedScreenshot || null,
          videoType,
          saveMode,
          editScaffoldID: scaffold?.ID ?? undefined
        });
        onHide();
        return;
      }

      const token = getTokenForRequest();
      const topicID = editQuestionID ? null : selectedTopic;
      const status = saveMode === 'submit' ? 'P' : (saveMode === 'draft' ? 'D' : 'A'); // P=Pending, D=Draft, A=Approved

      const apiEndpoint = scaffold?.ID
        ? './webservices/Scaffold.svc/UpdateScaffold'
        : './webservices/Scaffold.svc/AddScaffold';

      // Send strings as-is in JSON; do not URL-encode (avoids double-encode / broken HTML for TinyMCE content)
      const payload = scaffold?.ID
        ? {
            token: window.encodeToken(token),
            scaffoldId: String(scaffold.ID),
            description: desc ?? '',
            filePath: uploadedFile ?? '',
            scaffoldType: fileType ?? '',
            topicID,
            status,
            screenshot: uploadedScreenshot != null && uploadedScreenshot !== '' ? uploadedScreenshot : null,
            videoType: videoType
          }
        : {
            token: window.encodeToken(token),
            description: desc ?? '',
            fileId: uploadedFile ?? '',
            type: fileType ?? '',
            topicID,
            status,
            screenshot: uploadedScreenshot != null && uploadedScreenshot !== '' ? uploadedScreenshot : null,
            videoType: videoType
          };

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to save scaffold');
      }

      const result = await response.json();

      if (result.Success === false) {
        setError(result.ErrorMessage || 'Failed to save scaffold. Please try again.');
        return;
      }

      if (onSave) {
        onSave(result, saveMode);
      }

      onHide();
    } catch (error) {
      console.error('Error saving scaffold:', error);
      setError('Failed to save scaffold');
    }
  };

  if (!show) return null;

  const isEditMode = !!scaffold;
  const isFromQuestion = !!editQuestionID;

  return (
    <>
      <div className="modal fade show react-modal-fix scaffold-editor-modal" style={{ display: 'block' }} role="dialog">
        <div className="modal-dialog scaffold-editor-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                {isEditMode ? 'Edit Scaffold' : 'Add Scaffold'}
              </h4>
              <button
                type="button"
                className="btn-close-x"
                aria-label="Close"
                onClick={onHide}
              >×</button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="alert alert-danger scaffoldMessage">
                  {error}
                </div>
              )}

              {/* Mode Selection: Create vs Link - show on both standalone and add-question page */}
              <div className="radios">
                <table width="100%">
                  <tbody>
                    <tr>
                      <td width="280">
                        <span>
                          <span className="right-margin1">
                            <input
                              type="radio"
                              name="scaffoldRadio"
                              className="createRadio right-margin"
                              checked={scaffoldMode === 'create'}
                              onChange={() => setScaffoldMode('create')}
                            />
                          </span>
                          <span className="create-scaffold-label">Create a new scaffold</span>
                        </span>
                      </td>
                      <td>
                        <span>
                          <span className="right-margin1">
                            <input
                              type="radio"
                              name="scaffoldRadio"
                              className="linkRadio right-margin"
                              checked={scaffoldMode === 'link'}
                              onChange={() => setScaffoldMode('link')}
                            />
                          </span>
                          <span className="link-scaffold-label">Link an existing scaffold</span>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="top-margin">
                {/* Link mode: search + results + linkFile */}
                {scaffoldMode === 'link' && (
                  <div className="link-scaffold-section">
                    <div className="search-scaffold">
                      <input
                        type="text"
                        className="form-control typeahead search-scaffold-input"
                        placeholder="Search Scaffolds"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                    <div className="scaffold-search-results">
                      {filteredScaffolds.map((s) => (
                        <div
                          key={s.ID}
                          className={`scaffold-result-item ${linkedScaffold?.ID === s.ID ? 'selected' : ''}`}
                          onClick={() => handleScaffoldSelect(s)}
                        >
                          <strong>{s.TypeDesc}</strong> - {htmlToPlainText(s.Description ?? s.description).substring(0, 100)}
                        </div>
                      ))}
                    </div>
                    {linkedScaffold && (
                      <div className="linkFile top-margin">
                        <label className="label label-info right-margin">{linkedScaffold.TypeDesc}</label>
                        <span className="h4">{linkedScaffold.Filename || linkedScaffold.FileID}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Description (matches old: h5 + mandatory text + textarea) */}
                <div>
                  <h5><b>Description</b></h5>
                  <span>Description is mandatory</span>
                </div>
                <div className="top-margin">
                  <textarea
                    id="scaffoldArea"
                    className="form-control"
                    style={{ minHeight: '120px' }}
                  />
                </div>
              </div>

              {/* Topic (create mode only, matches old topicSelect) */}
              {!isFromQuestion && scaffoldMode === 'create' && (
                <div className="topicSelect top-margin">
                  <span className="field-required-mark">*</span>
                  <span className="topic-label">Topic :</span>
                  <select
                    className="scaffoldTopicSelect form-control"
                    value={selectedTopic || ''}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                  >
                    <option value="">Please choose a topic ...</option>
                    {topics.map((topic) => (
                      <option key={topic.ID} value={topic.ID}>
                        {topic.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Video type (create mode only, matches old scaffoldVideoType) */}
              {!isFromQuestion && scaffoldMode === 'create' && (
                <div className="top-margin scaffoldVideoType">
                  <div>
                    <label>Please select video type if you are uploading a video</label>
                  </div>
                  <span>
                    <label className="scaffoldTypeText">
                      <input
                        type="radio"
                        name="scaffoldVideoType"
                        id="scaffoldVideoTypeQuestion"
                        value="Q"
                        checked={videoType === 'Q'}
                        onChange={(e) => setVideoType(e.target.value)}
                      />
                      Question Scaffold
                    </label>
                  </span>
                  <span>
                    <label className="scaffoldTypeText scaffoldVideoTypeTopic">
                      <input
                        type="radio"
                        name="scaffoldVideoType"
                        id="scaffoldVideoTypeTopic"
                        value="T"
                        checked={videoType === 'T'}
                        onChange={(e) => setVideoType(e.target.value)}
                      />
                      Topic Scaffold
                    </label>
                  </span>
                </div>
              )}

              {/* File upload (create mode only) */}
              {scaffoldMode === 'create' && (
                <div className="fileContainer">
                  {!uploadedFile && (
                    <div className="scaffold-file-upload">
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="scaffoldFileUpload"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                        accept="image/*,audio/*,video/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msexcel,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/zip"
                      />
                      <button
                        type="button"
                        className="btn btn-success fileinput-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <i className="glyphicon glyphicon-plus"></i>
                        <span>Select file...</span>
                      </button>
                    </div>
                  )}

                  {uploadedFile && (
                    <div className="scaffold-files">
                      <span className="label label-info">{fileType}</span>
                      <span style={{ marginLeft: '10px' }}>{uploadedFile}</span>
                      <button
                        type="button"
                        className="scaffold-remove-x"
                        onClick={() => {
                          setUploadedFile(null);
                          setFileType(null);
                          setUploadedScreenshot(null);
                        }}
                        title="Remove file"
                        aria-label="Remove file"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {uploadedFile && fileType === 'Video' && !uploadedScreenshot && (
                    <div className="scaffold-file-upload" style={{ marginTop: '10px' }}>
                      <input
                        ref={screenshotInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleScreenshotSelect}
                      />
                      <button
                        type="button"
                        className="btn btn-success fileinput-button"
                        onClick={() => screenshotInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <span>Upload Screenshot</span>
                      </button>
                    </div>
                  )}

                  {uploadedScreenshot && (
                    <div className="scaffold-files" style={{ marginTop: '10px' }}>
                      <span className="label label-info">Screenshot</span>
                      <span style={{ marginLeft: '10px' }}>{uploadedScreenshot}</span>
                      <button
                        type="button"
                        className="scaffold-remove-x"
                        onClick={() => setUploadedScreenshot(null)}
                        title="Remove screenshot"
                        aria-label="Remove screenshot"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {uploading && (
                    <div className="upload-progress" style={{ marginTop: '15px' }}>
                      <div className="progress">
                        <div
                          className="progress-bar progress-bar-success"
                          style={{ width: `${uploadProgress}%` }}
                        >
                          {Math.round(uploadProgress)}%
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', marginTop: '10px' }}>
                        <span className="upload-info">{uploadInfo}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-default"
                          onClick={cancelUpload}
                          style={{ marginLeft: '10px' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              {/* Link button: only when "Link an existing scaffold" is selected */}
              {scaffoldMode === 'link' && (
                <button
                  type="button"
                  className="btn btn-primary linkScaffoldButton"
                  onClick={() => handleSave('link')}
                  disabled={!linkedScaffold || uploading}
                >
                  Link
                </button>
              )}
              {/* Submit for Approval and Save Draft: only when "Create a new scaffold" is selected */}
              {scaffoldMode === 'create' && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary addScaffoldButton"
                    onClick={() => handleSave('submit')}
                    disabled={uploading || !uploadedFile}
                  >
                    Submit for Approval
                  </button>
                  <button
                    type="button"
                    className="btn btn-success addScaffoldButton"
                    onClick={() => handleSave('draft')}
                    disabled={uploading || !uploadedFile}
                  >
                    Save Draft
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-default"
                onClick={onHide}
                disabled={uploading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
    </>
  );
};

export default ScaffoldEditor;

